import * as THREE from 'three';
import { createTranslationMatrix } from './utils/matrix';
import { viewerState } from './state/viewer.svelte';
function setObjectMatrix(object, matrix) {
    object.matrix.copy(matrix);
    object.matrixAutoUpdate = false;
    // copy() does not dirty matrixWorld. This matters when the object rendered
    // once between scene.add() and transform assignment (the normal load path).
    object.matrixWorldNeedsUpdate = true;
}
export function updateCameraMatrix(host) {
    // Create a matrix that represents the camera's current position and rotation
    host.cameraMatrix.identity();
    // Apply camera position
    const positionMatrix = new THREE.Matrix4();
    positionMatrix.makeTranslation(-host.camera.position.x, -host.camera.position.y, -host.camera.position.z);
    // Apply camera rotation (inverse of camera quaternion)
    const rotationMatrix = new THREE.Matrix4();
    rotationMatrix.makeRotationFromQuaternion(host.camera.quaternion.clone().invert());
    // Combine position and rotation
    host.cameraMatrix.multiply(rotationMatrix).multiply(positionMatrix);
}
export function setTransformationMatrix(host, fileIndex, matrix) {
    if (fileIndex >= 0 && fileIndex < host.transformationMatrices.length) {
        host.transformationMatrices[fileIndex].copy(matrix);
        applyTransformationMatrix(host, fileIndex);
    }
}
export function getTransformationMatrix(host, fileIndex) {
    if (fileIndex >= 0 && fileIndex < host.transformationMatrices.length) {
        return host.transformationMatrices[fileIndex].clone();
    }
    return new THREE.Matrix4(); // Return identity matrix if index is invalid
}
export function getTransformationMatrixAsArray(host, fileIndex) {
    if (fileIndex >= 0 && fileIndex < host.transformationMatrices.length) {
        return host.transformationMatrices[fileIndex].elements.slice();
    }
    return new THREE.Matrix4().elements.slice(); // Return identity matrix if index is invalid
}
export function applyTransformationMatrix(host, fileIndex) {
    if (fileIndex < 0 || fileIndex >= host.transformationMatrices.length) {
        return;
    }
    const matrix = host.transformationMatrices[fileIndex];
    host.sectionPlanes?.updateTransform(fileIndex, matrix);
    // Handle PLY/mesh files
    if (fileIndex < host.meshes.length) {
        const mesh = host.meshes[fileIndex];
        if (mesh) {
            setObjectMatrix(mesh, matrix);
        }
        // Also apply transformation to vertex points visualization
        const vertexPoints = host.vertexPointsObjects[fileIndex];
        if (vertexPoints) {
            setObjectMatrix(vertexPoints, matrix);
        }
        // Also apply transformation to normals visualizer
        const normalsVisualizer = host.normalsVisualizers[fileIndex];
        if (normalsVisualizer) {
            setObjectMatrix(normalsVisualizer, matrix);
        }
        // Also apply transformation to multi-material groups (for OBJ files)
        const multiMaterialGroup = host.multiMaterialGroups[fileIndex];
        if (multiMaterialGroup) {
            setObjectMatrix(multiMaterialGroup, matrix);
        }
        // Also mirror onto the splat mesh when this file renders as splats
        host.splatMode?.applyMatrix(fileIndex, matrix);
        return;
    }
    // Handle poses
    const poseIndex = fileIndex - host.spatialFiles.length;
    if (poseIndex >= 0 && poseIndex < host.poseGroups.length) {
        const group = host.poseGroups[poseIndex];
        if (group) {
            setObjectMatrix(group, matrix);
        }
        return;
    }
    // Handle cameras
    const cameraIndex = fileIndex - host.spatialFiles.length - host.poseGroups.length;
    if (cameraIndex >= 0 && cameraIndex < host.cameraGroups.length) {
        const group = host.cameraGroups[cameraIndex];
        if (group) {
            // Apply transformation matrix to camera profile group
            setObjectMatrix(group, matrix);
            // Apply scaling only to visual elements, not position
            const size = host.pointSizes[fileIndex] ?? 1.0;
            host.applyCameraScale(cameraIndex, size);
        }
    }
}
export function resetTransformationMatrix(host, fileIndex) {
    if (fileIndex >= 0 && fileIndex < host.transformationMatrices.length) {
        host.transformationMatrices[fileIndex].identity();
        applyTransformationMatrix(host, fileIndex);
    }
}
export function multiplyTransformationMatrices(host, fileIndex, matrix) {
    if (fileIndex >= 0 && fileIndex < host.transformationMatrices.length) {
        host.transformationMatrices[fileIndex].multiply(matrix);
        applyTransformationMatrix(host, fileIndex);
    }
}
export function addTranslationToMatrix(host, fileIndex, x, y, z) {
    if (fileIndex >= 0 && fileIndex < host.transformationMatrices.length) {
        const translationMatrix = createTranslationMatrix(x, y, z);
        multiplyTransformationMatrices(host, fileIndex, translationMatrix);
    }
}
export function updateMatrixTextarea(host, fileIndex) {
    const textarea = document.getElementById(`matrix-${fileIndex}`);
    if (textarea) {
        const matrixArr = getTransformationMatrixAsArray(host, fileIndex);
        let matrixStr = '';
        // Three.js stores matrices in column-major order: [m00, m10, m20, m30, m01, m11, m21, m31, m02, m12, m22, m32, m03, m13, m23, m33]
        // Display in row-major order to match the input format: each row should be [m0r, m1r, m2r, m3r]
        for (let row = 0; row < 4; ++row) {
            const displayRow = [
                matrixArr[row], // m0r (column r, row 0)
                matrixArr[row + 4], // m1r (column r, row 1)
                matrixArr[row + 8], // m2r (column r, row 2)
                matrixArr[row + 12], // m3r (column r, row 3)
            ].map(v => {
                // Format numbers consistently: 6 decimal places, no padding
                return v.toFixed(6);
            });
            matrixStr += displayRow.join(' ') + '\n';
        }
        textarea.value = matrixStr.trim();
    }
}
export function updateCameraMatrixDisplay(_host) {
    // Camera matrix is now displayed in the camera controls panel
    // This method is kept for compatibility but doesn't display anything
}
export function updateCameraControlsPanel(host) {
    // Phase 4 (docs/SVELTE_MIGRATION_PLAN.md): CameraControlsPanel.svelte reads
    // these reactively instead of this function building/patching innerHTML.
    const pos = host.camera.position;
    const euler = new THREE.Euler();
    euler.setFromQuaternion(host.camera.quaternion, 'XYZ');
    const rotX = (euler.x * 180) / Math.PI;
    const rotY = (euler.y * 180) / Math.PI;
    const rotZ = (euler.z * 180) / Math.PI;
    const target = host.controls.target;
    viewerState.cameraFov = host.camera.fov;
    viewerState.cameraNear = host.camera.near;
    viewerState.cameraFar = host.camera.far;
    viewerState.cameraPositionText = `(${pos.x.toFixed(3)}, ${pos.y.toFixed(3)}, ${pos.z.toFixed(3)})`;
    viewerState.cameraRotationText = `(${rotX.toFixed(1)}°, ${rotY.toFixed(1)}°, ${rotZ.toFixed(1)}°)`;
    viewerState.cameraTargetText = `(${target.x.toFixed(3)}, ${target.y.toFixed(3)}, ${target.z.toFixed(3)})`;
}
